import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useBlockWidth } from '../../../studio-blocks/contexts/BlockWidthContext';
import { useFilteredRequestsAndAnalyses } from '../../utils/hooks/use-filtered-requests';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  allProxyInfoState,
  analysisSubjectQuery,
  blockColumnHeaderHeightState,
  blockDataRowHeightState,
  blockDisplayHeader,
  blockExportMetadata,
  blockFactorIds,
  blockFactors,
  blockFonts,
  blockFullContentHeightState,
  blockInfoGraphicType,
  blockTitleBarHeightState,
  customizedBlock,
  getCustomFontSize,
  isReportState,
  viewDataGridSizeType,
} from 'venn-state';
import { useGetRequestSubjectFee } from '../../utils/hooks/use-advisory-fee';
import type { AnalysisProps } from '../../types';
import { getSubjectFromRequestSubject, measureString, ptToPx, useHasFF } from 'venn-utils';
import factorLensesContext from '../../../contexts/factor-lenses-context';
import { ThemeContext } from 'styled-components';
import {
  numberTopLegendFormatter,
  percentTopLegendFormatter,
} from '../../../studio-blocks/components/charts/heatMapUtils';
import {
  convertHeatMapDataToExcel,
  generalDataToFactorTrendData,
  mapAnalysisTypeToFactorTrendDataType,
} from '../../../studio-blocks/components/charts/factorTrendUtils';
import { max } from 'lodash';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import { GetTypography } from 'venn-ui-kit';
import { FactorTrendNoData } from '../../../studio-blocks/components/charts/FactorTrendNoData';
import { getProformaPortfolioPalette } from '../../../heat-map/utils';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';
import HeatMap, { topMargin, BOTTOM_SPACE } from '../../../heat-map/HeatMap';
import { BlockContext } from '../../contexts/block-context';
import { MESSAGE_HEIGHT } from '../../../factor-chart';
import { useCustomSlice } from '../../utils/hooks/use-custom-slice';
import { useAppPrintMode } from '../../../print';
import { DataGridSizeType } from '../../../studio-blocks';
import {
  convertLineDataToExcel,
  parseLineChartItems,
} from '../../../studio-blocks/components/charts/factor-line/factorLineUtils';
import { FactorTrendLineChart } from '../../../studio-blocks/components/charts/factor-line/FactorTrendLineChart';
import { useGetBlockSubjectSuperscripts } from '../../components/core/block-subject-footnotes-and-superscripts';

const RIGHT_MARGIN = 4;
const HEATMAP_COLUMN_HEADER_HEIGHT_PX = topMargin + BOTTOM_SPACE;

export const FactorTrendContent = ({ id }: { id: string }) => {
  const { inPrintModeOrReportLab } = useAppPrintMode();

  const { requests, analyses } = useFilteredRequestsAndAnalyses(id);

  const isReport = useRecoilValue(isReportState);
  const infoGraphicType = useRecoilValue(blockInfoGraphicType(id));

  const isCompact = useRecoilValue(viewDataGridSizeType(id)) === DataGridSizeType.COMPACT;
  const exportMetaData = useRecoilValue(blockExportMetadata(id));

  return infoGraphicType === 'HEATMAP' ? (
    <HeatMapChart
      id={id}
      requests={requests}
      analyses={analyses}
      inPrintMode={inPrintModeOrReportLab}
      isCompact={isCompact}
      exportMetaData={exportMetaData}
      isReport={isReport}
    />
  ) : infoGraphicType === 'LINE' ? (
    <LineChart
      id={id}
      requests={requests}
      analyses={analyses}
      inPrintMode={inPrintModeOrReportLab}
      isCompact={isCompact}
      exportMetaData={exportMetaData}
      isReport={isReport}
    />
  ) : null;
};

const HeatMapChart = ({ id, inPrintMode, isReport, requests, analyses, exportMetaData }: AnalysisProps) => {
  const metrics = useSelectedMetrics();
  const hasCanvasHeatmap = useHasFF('canvas_heatmap_ff');
  const allProxy = useRecoilValue(allProxyInfoState);
  const axisFont = useRecoilValue(blockFonts.blockChartAxis(id));
  const legendFont = useRecoilValue(blockFonts.blockChartLegend(id));
  const title = useRecoilValue(blockDisplayHeader(id));
  const { primaryFactorLens } = useContext(factorLensesContext);
  const theme = useContext(ThemeContext);
  const subject = requests[0]!.subject;
  const analysisSubject = useRecoilValue(analysisSubjectQuery(getSubjectFromRequestSubject(subject)));
  const selectedFactors = useRecoilValue(blockFactorIds(id));
  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  const nameSuperscript = getBlockSubjectSuperscripts({ subject, isBenchmark: false });
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const currentMetric = metrics[0];
  const showAsPercentage = currentMetric?.dataType === 'PERCENTAGE';
  const topLegendFormatter = showAsPercentage ? percentTopLegendFormatter : numberTopLegendFormatter;
  const metricType = currentMetric && mapAnalysisTypeToFactorTrendDataType[currentMetric.analysisResultKey!];
  const metricTypeForHeatMap = metricType === 'return' || metricType === 'risk' ? metricType : undefined;
  const defaultFactors = primaryFactorLens?.factors || [];
  const factorsDescriptions = defaultFactors.map(({ id, description }) => ({ id, description }));

  const { series, timestamps, exportableSeries, analyzedFactors } = useMemo(
    () =>
      generalDataToFactorTrendData({
        metric: metrics[0],
        requests,
        analysisSubject,
        analyses,
        activeFactorLens: primaryFactorLens,
        theme,
        selectedFactors,
        allProxy,
        getRequestSubjectFee,
      }),
    [
      metrics,
      requests,
      analysisSubject,
      analyses,
      primaryFactorLens,
      theme,
      selectedFactors,
      allProxy,
      getRequestSubjectFee,
    ],
  );

  const excelDataFn = useCallback(
    () =>
      convertHeatMapDataToExcel(exportableSeries, timestamps, title, false, currentMetric?.dataType === 'PERCENTAGE') ||
      [],
    [currentMetric?.dataType, exportableSeries, timestamps, title],
  );

  useExportUpdate({
    selectedRefId: id,
    exportMetaData,
    excelDataFn,
  });

  const width = useBlockWidth().raw.innerWidth;

  const cellHeight = ptToPx(axisFont.fontSizePt) * 1.85;
  const filterCount = defaultFactors.length - analyzedFactors;

  const titleBarHeight = useRecoilValue(blockTitleBarHeightState(id));
  const setDataRowHeight = useSetRecoilState(blockDataRowHeightState(id));
  const setColumnHeaderHeight = useSetRecoilState(blockColumnHeaderHeightState(id));
  const setFullContentHeight = useSetRecoilState(blockFullContentHeightState(id));

  const isContinuation = useContext(BlockContext)?.isContinuation;

  useEffect(() => {
    if (!isReport) return;
    setDataRowHeight(cellHeight);
    setColumnHeaderHeight(HEATMAP_COLUMN_HEADER_HEIGHT_PX);
    setFullContentHeight(
      titleBarHeight + topMargin + series.length * cellHeight + BOTTOM_SPACE + (filterCount > 0 ? MESSAGE_HEIGHT : 0),
    );
  }, [
    isReport,
    series.length,
    cellHeight,
    titleBarHeight,
    filterCount,
    setDataRowHeight,
    setColumnHeaderHeight,
    setFullContentHeight,
  ]);

  const displayedSeries = useCustomSlice(series, id);

  const factorsColumnWidth = useMemo(() => {
    const FACTOR_COLUMN_PADDING = 15;
    return (
      (max(
        series.map((serie) =>
          measureString(serie.name, {
            fontSize: getCustomFontSize(axisFont),
            fontFamily: GetTypography.fontFamily({ theme }),
            fontWeight: 'normal',
          }),
        ),
      ) ?? 0) +
      FACTOR_COLUMN_PADDING * 2
    );
  }, [axisFont, series, theme]);

  return (
    <div data-testid="qa-factor-trend-heat-map-parent">
      {!series.length || !timestamps.length || !displayedSeries.length ? (
        <FactorTrendNoData />
      ) : (
        <HeatMap
          isPercentage={showAsPercentage}
          factorsColumnWidth={factorsColumnWidth}
          metricType={metricTypeForHeatMap}
          palette={getProformaPortfolioPalette(theme.Colors)}
          factorsDescriptions={factorsDescriptions}
          print={!!inPrintMode}
          showSubjectsValues={!inPrintMode}
          series={displayedSeries}
          seriesColumnWidth={inPrintMode ? 0 : undefined}
          filterCount={isContinuation ? 0 : filterCount}
          seriesDataSize={timestamps.length}
          timestamps={timestamps}
          topLegendFormatter={topLegendFormatter}
          width={width - RIGHT_MARGIN}
          cellHeight={cellHeight}
          displaySubjectNames
          nameSuperscript={nameSuperscript}
          theme={theme}
          useCanvas={hasCanvasHeatmap}
          axisFontSize={getCustomFontSize(axisFont)}
          legendFontSize={getCustomFontSize(legendFont)}
          marginTop="-10px"
          exportMarginTop="-10px"
        />
      )}
    </div>
  );
};

const LineChart = ({ id, analyses, requests, exportMetaData }: AnalysisProps) => {
  const metrics = useSelectedMetrics();
  const theme = useContext(ThemeContext);
  const title = useRecoilValue(blockDisplayHeader(id));
  const allProxy = useRecoilValue(allProxyInfoState);
  const selectedBlock = useRecoilValue(customizedBlock(id));
  const factorMetrics = useRecoilValue(blockFactors(id));
  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const currentMetric = metrics[0];
  const showAsPercentage = currentMetric?.dataType === 'PERCENTAGE';

  const baseItems = useMemo(
    () =>
      parseLineChartItems({
        analyses,
        requests,
        selectedBlock,
        factorMetrics,
        colors: theme.Colors,
        currentMetric,
        allProxy,
        getRequestSubjectFee,
      }),
    [analyses, requests, selectedBlock, factorMetrics, theme.Colors, currentMetric, allProxy, getRequestSubjectFee],
  );

  const items = useMemo(
    () =>
      baseItems.map((item) => {
        if (!item.subject) return item;
        const superscript = getBlockSubjectSuperscripts({
          subject: item.subject,
          isBenchmark: !!item.isBenchmark,
        });
        if (!superscript) return item;
        return {
          ...item,
          seriesTypes: {
            ...item.seriesTypes,
            superscript,
          },
        };
      }),
    [baseItems, getBlockSubjectSuperscripts],
  );

  const excelDataFn = useCallback(
    () => convertLineDataToExcel(items, title, currentMetric?.dataType === 'PERCENTAGE') || [],
    [items, title, currentMetric?.dataType],
  );

  useExportUpdate({ selectedRefId: id, exportMetaData, excelDataFn });

  return (
    <div className="min-w-0 flex-1" data-testid="qa-factor-trend-line-chart-parent">
      {!analyses.length ? (
        <FactorTrendNoData />
      ) : (
        <FactorTrendLineChart items={items} displayOptions={{ percentageValue: showAsPercentage }} />
      )}
    </div>
  );
};
